import { useCallback, useEffect, useState } from "react";
import { useLatestRequest } from "../../../hooks/useLatestRequest";
import { getApiErrorMessage } from "../../../lib/result-codes";
import { getUserStatistics } from "../api/users.api";
import type { UserStatistics } from "../types/user.types";

export interface UseUserStatisticsReturn {
  data: UserStatistics | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useUserStatistics(): UseUserStatisticsReturn {
  const [data, setData] = useState<UserStatistics | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { run, loading } = useLatestRequest();

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const result = await run((signal) => getUserStatistics(signal));
      if (result) setData(result);
    } catch (requestError: unknown) {
      setData(null);
      setError(getApiErrorMessage(requestError, "Failed to fetch user statistics"));
    }
  }, [run]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { data, loading, error, refresh };
}
